import { useEffect, useState } from "react";
import { Button } from "./Button";

/**
 * Strictly-necessary-only cookie banner (PLAN §4 Phase 4 / GDPR essentials).
 *
 * Behaviour:
 *  - First visit: banner renders fixed to the bottom of the viewport.
 *  - User picks "Accept all" or "Strictly necessary only".
 *  - Choice is persisted in localStorage at `cura-studio:cookie-consent`
 *    with the value `"all"` or `"essential"`.
 *  - Once a choice is recorded, the banner stays hidden across visits.
 *  - A re-prompt can be triggered from anywhere via the
 *    `cura-studio:cookie-consent:reopen` window event — the Footer's
 *    "Cookies preferences" link fires it.
 *
 * Voice: plain English, no dark patterns, no scary capitalised buttons,
 * "essential" and "all" both styled as buttons of equivalent weight.
 */

export const COOKIE_CONSENT_KEY = "cura-studio:cookie-consent";
export const COOKIE_CONSENT_REOPEN_EVENT = "cura-studio:cookie-consent:reopen";

type Choice = "all" | "essential";

function readChoice(): Choice | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(COOKIE_CONSENT_KEY);
  return v === "all" || v === "essential" ? v : null;
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  // On mount, decide whether to show. Avoid SSR mismatch by deferring to
  // an effect — the banner is decorative chrome, not a blocker.
  useEffect(() => {
    setVisible(readChoice() === null);
  }, []);

  // Listen for the Footer "Cookies preferences" link.
  useEffect(() => {
    const onReopen = () => setVisible(true);
    window.addEventListener(COOKIE_CONSENT_REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(COOKIE_CONSENT_REOPEN_EVENT, onReopen);
  }, []);

  if (!visible) return null;

  const record = (choice: Choice) => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_KEY, choice);
    } catch {
      // If localStorage is unavailable (private mode, quota), still hide
      // the banner for this session so it doesn't loop forever.
    }
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-[var(--border-1)] bg-white shadow-[0_-6px_18px_rgba(33,43,50,0.06)]"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm leading-relaxed text-fg-2">
          <strong className="font-semibold text-fg-1">Cookies on +CURA Studio.</strong>{" "}
          We use a small number of cookies to keep you signed in and to improve Studio.
          You can accept everything or just the essentials. Read our{" "}
          <a
            href="/files/cookies.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[color:var(--brand-link)] underline underline-offset-2 hover:no-underline"
          >
            Cookies policy
          </a>
          .
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button variant="secondary" onClick={() => record("essential")}>
            Strictly necessary only
          </Button>
          <Button
            onClick={() => record("all")}
            className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Trigger the banner to reappear — used by the Footer "Cookies preferences"
 * link. Lives here so the event name is owned in one place.
 */
export function reopenCookieBanner() {
  window.dispatchEvent(new Event(COOKIE_CONSENT_REOPEN_EVENT));
}
